import { useCallback, useEffect, useId, useRef, useState, type DragEvent, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { ConfirmCard, ResultCard } from '../../../shared/components/ConfirmCard';
import { employeeDocumentsGateway } from '../index';
import {
  CorrectionNotificationState,
  DocumentReviewStatus,
  DocumentSource,
  documentDisplayName,
  documentTypeDisplayLabel,
  EmployeeDocumentType,
  employeeDocumentTypeLabels,
  type EmployeeDocumentResponse,
} from '../../reports/types';
import { ACCEPT_ATTRIBUTE, checkFile, formatFileSize, ACCEPTED_DOCUMENT_TYPES } from '../types';
import '../../people/employee-list.css';
import './employee-documents.css';

type Filter = 'all' | 'paystubs';

const todayIso = () => new Date().toISOString().slice(0, 10);

const formatDate = (value: string | null) => {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : value;
};

const typeOptions = [EmployeeDocumentType.W4, EmployeeDocumentType.I9, EmployeeDocumentType.Paystub, EmployeeDocumentType.Other];

function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M1.5 8s2.3-4.5 6.5-4.5S14.5 8 14.5 8 12.2 12.5 8 12.5 1.5 8 1.5 8z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <circle cx="8" cy="8" r="2" fill="none" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 8.5h5.8l.6-8.5M7 7v4M9 7v4" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M14 2L7 9M14 2l-4.5 12-2.5-5.5L1.5 6 14 2z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 16V5M7.5 9.5L12 5l4.5 4.5M5 15v3.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RowAction({ label, tone, onClick, href, disabled, children }: {
  label: string; tone: 'edit' | 'danger'; onClick?: () => void; href?: string; disabled?: boolean; children: ReactNode;
}) {
  const className = `row-action row-action--${tone}`;
  const content = (<>{children}<span className="row-action__label" aria-hidden="true">{label}</span></>);
  if (href) {
    return (
      <a className={className} href={href} target="_blank" rel="noreferrer" aria-label={label} title={label}>{content}</a>
    );
  }
  return (
    <button type="button" className={className} aria-label={label} title={label} onClick={onClick} disabled={disabled}>{content}</button>
  );
}

interface Selected {
  file: File;
}

interface Dialog {
  kind: 'archive' | 'correction';
  document: EmployeeDocumentResponse;
}

/** Expiry facts are separate from storage and review: no date is not a validity claim, and a past date is shown as Expired but never deletes anything. */
function expiryState(doc: EmployeeDocumentResponse, today: string): 'none' | 'expired' | 'expires' {
  if (!doc.expiryDate) return 'none';
  return doc.expiryDate < today ? 'expired' : 'expires';
}

/**
 * Admin Employee Profile Setup, Step 3: Upload Documents (Figma admin Step 3). One implementation, employee-scoped, used by the profile step and by the
 * Reports > Documents page.
 *
 * What the screen promises, and does not:
 *  - "On File" means the server stored the file. It does not mean the document is valid, reviewed or approved. Review is a separate fact (Pending Review,
 *    Needs Correction) and expiry is a third; nothing is ever shown as Verified because no verification step exists.
 *  - A selected file is not an upload. A row appears only after the server confirms the write, and a failed upload keeps the form so it can be retried
 *    without creating a duplicate.
 *  - What an administrator may do depends on who uploaded the file, and the server enforces it: an administrator's own file can be archived (kept, not
 *    deleted); an employee's own file cannot be removed, only a correction requested, with a reason, keeping the original.
 *  - Employee Paystubs is a filtered view of the same documents. A paystub here is a reference file and never feeds payroll.
 */
export function EmployeeDocumentsManager({ employeeId, footer }: { employeeId: string; footer?: ReactNode }) {
  const [documents, setDocuments] = useState<EmployeeDocumentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  const [selected, setSelected] = useState<Selected | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [documentName, setDocumentName] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [customType, setCustomType] = useState('');
  const [expiry, setExpiry] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [reason, setReason] = useState('');
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ title: string; text: string } | null>(null);

  const fileInput = useRef<HTMLInputElement>(null);
  const nameId = useId();
  const typeId = useId();
  const customId = useId();
  const expiryId = useId();
  const reasonId = useId();
  const today = todayIso();

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setDocuments(await employeeDocumentsGateway.list(employeeId));
    } catch {
      setLoadError('Could not load the documents.');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const dismissNotice = useCallback(() => setNotice(null), []);

  const isOther = documentType === String(EmployeeDocumentType.Other);
  const formValid = selected !== null && documentName.trim() !== '' && documentType !== '' && (!isOther || customType.trim() !== '');

  function choose(file: File | undefined) {
    setUploadError(null);
    if (!file) return;
    const problem = checkFile(file);
    if (problem) {
      setSelected(null);
      setFileError(problem);
      return;
    }
    setFileError(null);
    setSelected({ file });
  }

  function clearFile() {
    setSelected(null);
    setFileError(null);
    setUploadError(null);
    if (fileInput.current) fileInput.current.value = '';
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    choose(e.dataTransfer.files?.[0]);
  }

  function onZoneKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInput.current?.click();
    }
  }

  async function upload(e: FormEvent) {
    e.preventDefault();
    if (!formValid || !selected || uploading) return;
    setUploading(true);
    setUploadError(null);
    try {
      const created = await employeeDocumentsGateway.upload(employeeId, {
        file: selected.file,
        documentType: Number(documentType),
        documentName: documentName.trim(),
        customTypeDescription: isOther ? customType.trim() : null,
        expiryDate: expiry || null,
      });
      // Only now, with the server's answer in hand, does the row appear and the form reset.
      setDocuments((prev) => [created, ...prev.filter((d) => d.id !== created.id)]);
      setSelected(null);
      setDocumentName('');
      setDocumentType('');
      setCustomType('');
      setExpiry('');
      if (fileInput.current) fileInput.current.value = '';
      setNotice({ title: 'Document Uploaded!', text: 'The document is on file for this employee.' });
    } catch (err) {
      // The selection and every field are kept so the same upload can be retried; no row was added.
      setUploadError(err instanceof Error ? err.message : 'The upload failed. Nothing was saved.');
    } finally {
      setUploading(false);
    }
  }

  function openDialog(kind: Dialog['kind'], document: EmployeeDocumentResponse) {
    setReason('');
    setDialogError(null);
    setDialog({ kind, document });
  }

  function closeDialog() {
    setDialog(null);
    setDialogError(null);
  }

  const needsArchiveReason = (doc: EmployeeDocumentResponse) =>
    doc.documentType === EmployeeDocumentType.W4 || doc.documentType === EmployeeDocumentType.I9;

  async function confirmArchive() {
    if (!dialog) return;
    setDialogBusy(true);
    setDialogError(null);
    try {
      await employeeDocumentsGateway.archive(employeeId, dialog.document.id, reason.trim() || null);
      setDocuments((prev) => prev.filter((d) => d.id !== dialog.document.id));
      setDialog(null);
      setNotice({ title: 'Document Archived!', text: 'The document was removed from the active list. The file is kept on record.' });
    } catch (err) {
      // The row stays: nothing was removed.
      setDialogError(err instanceof Error ? err.message : 'Could not archive this document. Nothing was changed.');
    } finally {
      setDialogBusy(false);
    }
  }

  async function confirmCorrection() {
    if (!dialog) return;
    setDialogBusy(true);
    setDialogError(null);
    try {
      const updated = await employeeDocumentsGateway.requestCorrection(employeeId, dialog.document.id, reason.trim());
      setDocuments((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
      setDialog(null);
      setNotice(
        updated.correctionNotification === CorrectionNotificationState.Sent
          ? { title: 'Correction Requested!', text: 'The employee was emailed. Their original file stays on record.' }
          : { title: 'Correction Requested!', text: 'The request is saved, but the employee could not be emailed. Their original file stays on record.' },
      );
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Could not send the request. Nothing was changed.');
    } finally {
      setDialogBusy(false);
    }
  }

  const visible = documents.filter((d) => (filter === 'paystubs' ? d.documentType === EmployeeDocumentType.Paystub : true));
  const paystubCount = documents.filter((d) => d.documentType === EmployeeDocumentType.Paystub).length;

  return (
    <div className="edocs employee-list">
      <aside className="edocs-upload" aria-label="Upload a document">
        <form onSubmit={upload} noValidate>
          <input
            ref={fileInput}
            type="file"
            className="edocs-file-input"
            accept={ACCEPT_ATTRIBUTE}
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => choose(e.target.files?.[0])}
          />

          {selected ? (
            <div className="edocs-chip" data-testid="selected-file">
              <div className="edocs-chip__text">
                <span className="edocs-chip__name">{selected.file.name}</span>
                <span className="edocs-chip__meta">
                  {formatFileSize(selected.file.size)} · {ACCEPTED_DOCUMENT_TYPES[selected.file.type]} · <span className="edocs-chip__ready">Ready to upload</span>
                </span>
              </div>
              <button type="button" className="edocs-chip__remove" aria-label={`Remove ${selected.file.name}`} onClick={clearFile} disabled={uploading}>×</button>
            </div>
          ) : (
            <div
              className={`edocs-drop${dragging ? ' edocs-drop--active' : ''}`}
              role="button"
              tabIndex={0}
              aria-label="Choose a file to upload"
              onClick={() => fileInput.current?.click()}
              onKeyDown={onZoneKey}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
            >
              <UploadIcon />
              <span>Click to upload your file</span>
            </div>
          )}
          {fileError && <p className="error" role="alert">{fileError}</p>}
          <p className="edocs-hint">Upload one file at a time. Only PDF, JPG or PNG files are supported. Maximum file size: 10 MB per file.</p>

          <label htmlFor={nameId}>Document Name <span aria-hidden="true" className="edocs-req">*</span></label>
          <input id={nameId} value={documentName} maxLength={100} autoComplete="off" aria-required="true" disabled={uploading} onChange={(e) => setDocumentName(e.target.value)} />

          <label htmlFor={typeId}>Document Type <span aria-hidden="true" className="edocs-req">*</span></label>
          <select id={typeId} value={documentType} aria-required="true" disabled={uploading} onChange={(e) => setDocumentType(e.target.value)}>
            <option value="">Select a type</option>
            {typeOptions.map((t) => (
              <option key={t} value={t}>{employeeDocumentTypeLabels[t]}</option>
            ))}
          </select>

          {isOther && (
            <>
              <label htmlFor={customId}>Specify Document Type <span aria-hidden="true" className="edocs-req">*</span></label>
              <input id={customId} value={customType} maxLength={100} autoComplete="off" aria-required="true" disabled={uploading} onChange={(e) => setCustomType(e.target.value)} />
            </>
          )}

          <label htmlFor={expiryId}>Expiry Date <span className="edocs-optional">(optional)</span></label>
          <input id={expiryId} type="date" value={expiry} disabled={uploading} onChange={(e) => setExpiry(e.target.value)} />

          {uploadError && (
            <div className="edocs-failure" role="alert">
              <p className="error">{uploadError}</p>
              <p className="muted">Nothing was saved. Your file and entries are kept, so you can try again.</p>
            </div>
          )}

          <button type="submit" className="edocs-upload-btn" disabled={!formValid || uploading}>
            {uploading ? 'Uploading…' : uploadError ? 'Retry Upload' : 'Upload Document'}
          </button>
        </form>
      </aside>

      <section className="edocs-list profile-step-content" aria-label="Documents on file">
        <h3 className="profile-section-title">Upload Documents</h3>

        <div className="edocs-tabs" role="tablist" aria-label="Document views">
          <button type="button" role="tab" aria-selected={filter === 'all'} className={filter === 'all' ? 'edocs-tab edocs-tab--active' : 'edocs-tab'} onClick={() => setFilter('all')}>
            All Documents <span className="edocs-count">{documents.length}</span>
          </button>
          <button type="button" role="tab" aria-selected={filter === 'paystubs'} className={filter === 'paystubs' ? 'edocs-tab edocs-tab--active' : 'edocs-tab'} onClick={() => setFilter('paystubs')}>
            Employee Paystubs <span className="edocs-count">{paystubCount}</span>
          </button>
        </div>

        {loadError && <p className="error" role="alert">{loadError}</p>}

        <div className="employee-list__table-wrap edocs-table-wrap">
          <table className="employee-list__table edocs-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Document</th>
                <th scope="col">Document Type</th>
                <th scope="col">Upload Date</th>
                <th scope="col">Expiry Date</th>
                <th scope="col">Status</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="edocs-empty">Loading…</td></tr>}
              {!loading && visible.length === 0 && (
                <tr>
                  <td colSpan={7} className="edocs-empty">
                    {filter === 'paystubs' ? 'No paystubs on file for this employee.' : 'No documents on file for this employee yet.'}
                  </td>
                </tr>
              )}
              {visible.map((doc) => {
                const expiry = expiryState(doc, today);
                const fromEmployee = doc.source === DocumentSource.Employee;
                const needsCorrection = doc.reviewStatus === DocumentReviewStatus.NeedsCorrection;
                return (
                  <tr key={doc.id}>
                    <td>
                      <span className="edocs-name">{documentDisplayName(doc)}</span>
                      <span className="edocs-source">{fromEmployee ? 'Uploaded by employee' : 'Uploaded by admin'}</span>
                    </td>
                    <td className="edocs-file">{doc.originalFileName}</td>
                    <td>{documentTypeDisplayLabel(doc)}</td>
                    <td>{formatDate(doc.uploadedAtUtc)}</td>
                    <td>
                      {expiry === 'none' && <span className="muted">No expiry date</span>}
                      {expiry === 'expires' && formatDate(doc.expiryDate)}
                      {expiry === 'expired' && <span className="edocs-expired">{formatDate(doc.expiryDate)}</span>}
                    </td>
                    <td>
                      <span className="edocs-badges">
                        <span className="edocs-badge edocs-badge--onfile">On File</span>
                        <span className={`edocs-badge ${needsCorrection ? 'edocs-badge--correction' : 'edocs-badge--pending'}`}>
                          {needsCorrection ? 'Needs Correction' : 'Pending Review'}
                        </span>
                        {expiry === 'expired' && <span className="edocs-badge edocs-badge--expired">Expired</span>}
                      </span>
                      {needsCorrection && (
                        <span className="edocs-note">
                          Correction requested{doc.correctionRequestedAtUtc ? ` ${formatDate(doc.correctionRequestedAtUtc)}` : ''}
                          {doc.correctionNotification === CorrectionNotificationState.Sent ? ' · employee emailed' : ' · employee not emailed'}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="employee-list__actions">
                        <RowAction label="View" tone="edit" href={employeeDocumentsGateway.downloadUrl(employeeId, doc.id)}><EyeIcon /></RowAction>
                        {fromEmployee ? (
                          <RowAction
                            label={needsCorrection ? 'Correction requested' : 'Request correction'}
                            tone="edit"
                            disabled={needsCorrection}
                            onClick={() => openDialog('correction', doc)}
                          ><SendIcon /></RowAction>
                        ) : (
                          <RowAction label="Archive" tone="danger" onClick={() => openDialog('archive', doc)}><TrashIcon /></RowAction>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="edocs-legend">“On File” means the file is stored. It does not mean the document is valid, reviewed or approved.</p>

        {footer}
      </section>

      {dialog?.kind === 'archive' && (
        <ConfirmCard
          title="Archive this document?"
          body={`“${documentDisplayName(dialog.document)}” will leave this employee’s active documents. The file is kept on record and nothing else changes.`}
          cancelLabel="Cancel"
          confirmLabel="Yes, Archive"
          confirmTone="danger"
          busy={dialogBusy}
          error={dialogError}
          confirmDisabled={needsArchiveReason(dialog.document) && reason.trim() === ''}
          onCancel={closeDialog}
          onConfirm={() => void confirmArchive()}
        >
          <label htmlFor={reasonId}>
            Reason {needsArchiveReason(dialog.document) ? <span aria-hidden="true" className="edocs-req">*</span> : <span className="edocs-optional">(optional)</span>}
          </label>
          <textarea id={reasonId} value={reason} maxLength={500} aria-required={needsArchiveReason(dialog.document) || undefined} onChange={(e) => setReason(e.target.value)} />
        </ConfirmCard>
      )}

      {dialog?.kind === 'correction' && (
        <ConfirmCard
          title="Request a correction?"
          body={`The employee will be asked to correct or replace “${documentDisplayName(dialog.document)}”. Their original file stays on record.`}
          cancelLabel="Cancel"
          confirmLabel="Send Request"
          confirmTone="save"
          busy={dialogBusy}
          error={dialogError}
          confirmDisabled={reason.trim() === ''}
          onCancel={closeDialog}
          onConfirm={() => void confirmCorrection()}
        >
          <label htmlFor={reasonId}>Reason <span aria-hidden="true" className="edocs-req">*</span></label>
          <textarea id={reasonId} value={reason} maxLength={500} aria-required="true" onChange={(e) => setReason(e.target.value)} />
        </ConfirmCard>
      )}

      {notice && <ResultCard title={notice.title} text={notice.text} onDismiss={dismissNotice} />}
    </div>
  );
}
